import { VARIABLES_BY_KEY, type VariableKey } from '../lib/census-variables';
import { formatValue } from '../lib/format';
import { useI18n } from '../lib/i18n';
import type { MunicipioRecord, PrWide } from '../types';
import { ShareMenu } from './ShareMenu';

export function MunicipioPanel({
  record,
  variable,
  prWide,
  rank,
  total,
  onClose,
}: {
  record: MunicipioRecord | null;
  variable: VariableKey;
  prWide: PrWide;
  rank: number | null;
  total: number;
  onClose: () => void;
}) {
  const { t, lang } = useI18n();
  const noData = t('app.data.unavailable');

  if (!record) {
    return (
      <aside className="flex h-full w-full flex-col gap-3 border-an-rule bg-an-paper p-5 text-an-ink md:border-l">
        <p className="font-body text-sm text-an-muted">
          {t('app.panel.selectMunicipio')}
        </p>
      </aside>
    );
  }

  const meta = VARIABLES_BY_KEY[variable];
  const value = record[variable as keyof MunicipioRecord] as number | null;
  const prValue = prWide[variable] ?? null;
  const formatted = formatValue(value, meta.format, lang, noData);
  const prFormatted = formatValue(prValue, meta.format, lang, noData);

  const stats: Array<{ label: string; value: string }> = [
    {
      label: t('var.pct_black_female_of_black.short'),
      value: formatValue(record.pct_black_female_of_black, 'percent', lang, noData),
    },
    {
      label: t('var.pct_black_youth_under_19_of_black.short'),
      value: formatValue(
        record.pct_black_youth_under_19_of_black,
        'percent',
        lang,
        noData,
      ),
    },
    {
      label: t('var.black_poverty_rate.short'),
      value: formatValue(record.black_poverty_rate, 'percent', lang, noData),
    },
    {
      label: t('var.black_snap_rate.short'),
      value: formatValue(record.black_snap_rate, 'percent', lang, noData),
    },
  ];

  return (
    <aside className="relative flex h-full w-full flex-col gap-4 overflow-y-auto border-an-rule bg-an-paper p-5 helix-bg text-an-ink md:border-l">
      <button
        type="button"
        onClick={onClose}
        aria-label={t('app.panel.close')}
        className="absolute right-3 top-3 display border border-an-rule bg-an-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-an-black hover:text-an-white"
      >
        ×
      </button>

      <div>
        <h2 className="display text-3xl uppercase leading-none tracking-tight md:text-4xl">
          {record.name}
        </h2>
        <p className="mt-1 font-body text-xs text-an-muted">
          GEOID {record.GEOID}
          {rank != null && (
            <>
              {' · '}
              {t('app.panel.rank')} {rank} {t('app.panel.of')} {total}
            </>
          )}
        </p>
      </div>

      <div className="border-t border-b border-an-rule py-4">
        <p className="font-body text-[11px] uppercase tracking-widest text-an-muted">
          {t(`var.${variable}.label`)}
        </p>
        <p className="stat-num mt-1 text-5xl md:text-6xl">{formatted}</p>
        <p className="font-body text-xs text-an-muted">
          {t('app.panel.prAverage')}: {prFormatted}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="border border-an-rule bg-an-white p-3">
            <p className="font-body text-[10px] uppercase tracking-widest text-an-muted">
              {s.label}
            </p>
            <p className="stat-num mt-1 text-xl">{s.value}</p>
          </div>
        ))}
      </div>

      <CompareBars record={record} prWide={prWide} />

      <ShareMenu
        input={{
          geoid: record.GEOID,
          municipio: record.name,
          variable,
          formattedValue: formatted,
          lang,
          t,
        }}
      />
    </aside>
  );
}

function CompareBars({ record, prWide }: { record: MunicipioRecord; prWide: PrWide }) {
  const { t, lang } = useI18n();
  const noData = t('app.data.unavailable');
  const rows: Array<{ key: string; label: string; v: number | null; p: number | null }> = [
    {
      key: 'pct_black_alone',
      label: t('var.pct_black_alone.short'),
      v: record.pct_black_alone,
      p: prWide.pct_black_alone,
    },
    {
      key: 'black_poverty_rate',
      label: t('var.black_poverty_rate.short'),
      v: record.black_poverty_rate,
      p: prWide.black_poverty_rate,
    },
    {
      key: 'black_snap_rate',
      label: t('var.black_snap_rate.short'),
      v: record.black_snap_rate,
      p: prWide.black_snap_rate,
    },
    {
      key: 'black_lfpr',
      label: t('var.black_lfpr.short'),
      v: record.black_lfpr,
      p: prWide.black_lfpr,
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => {
        const max = Math.max(r.v ?? 0, r.p ?? 0, 0.0001);
        return (
          <div key={r.key} className="grid grid-cols-[100px_1fr_60px] items-center gap-2">
            <span className="font-body text-[11px] uppercase tracking-wider text-an-muted">
              {r.label}
            </span>
            <div className="relative h-4 bg-an-white ring-1 ring-an-rule">
              <div
                className="absolute inset-y-0 left-0 bg-an-black"
                style={{ width: `${((r.v ?? 0) / max) * 100}%` }}
              />
              <div
                className="absolute inset-y-0 border-l-2 border-an-ochre"
                style={{ left: `${((r.p ?? 0) / max) * 100}%` }}
                aria-label="PR avg"
              />
            </div>
            <span className="stat-num text-right text-xs">
              {formatValue(r.v, 'percent', lang, noData)}
            </span>
          </div>
        );
      })}
      <p className="font-body text-[10px] text-an-muted">
        <span className="inline-block h-2 w-2 bg-an-ochre align-middle" />{' '}
        {t('app.panel.prAverage')}
      </p>
    </div>
  );
}
